import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, FileDown, FileSpreadsheet, CheckCircle, XCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ProFeatureGate } from '../components/ProFeatureGate';
import { decodeBulk, decodeText } from '../utils/api';
import { exportToCSV, exportToExcel, exportValidOnly } from '../utils/export';

const BulkPage = () => {
  const [input, setInput] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const { isPro } = useAuth();
  const { t, language } = useLanguage();

  const handleDecode = async () => {
    if (!input.trim()) return;
    
    setLoading(true);
    
    try {
      // Split lines and clean
      const lines = input.split('\n').filter(line => line.trim());
      
      // Check for aanslagnummers (contain letters) or betalingskenmerken (16 digits)
      const kenmerken = lines.map(line => {
        const cleaned = line.trim().replace(/[\s\-\.]/g, '');
        // Accept both 16-digit betalingskenmerken and aanslagnummers with letters
        if (cleaned.length >= 10 && (cleaned.length === 16 || /[A-Za-z]/.test(cleaned))) {
          return cleaned.toUpperCase();
        }
        return null;
      }).filter(Boolean);
      
      if (kenmerken.length === 0) {
        // Try text extraction
        const data = await decodeText(input, language);
        setResults(data.results);
        setStats({ total: data.found, valid: data.valid, invalid: data.invalid });
      } else {
        const data = await decodeBulk(kenmerken, language);
        setResults(data.results);
        setStats({ total: data.total, valid: data.valid, invalid: data.invalid });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique tax types for filter
  const getTaxTypes = () => {
    if (!results) return [];
    const types = new Set();
    results.forEach(r => {
      if (r.valid && r.belastingtype?.nl) {
        types.add(r.belastingtype.nl);
      }
    });
    return Array.from(types);
  };

  const content = (
    <div className="py-12 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <Layers className="w-8 h-8 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-4">
            {t('bulk_title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
            {t('bulk_subtitle')}
          </p>
        </motion.div>

        {/* Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('bulk_placeholder')}
            className="w-full h-64 p-6 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-mono text-sm resize-none focus:border-accent focus:ring-4 focus:ring-accent/10"
            data-testid="bulk-input"
          />
          
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-slate-400">
              {language === 'nl' 
                ? 'Plak tekst met betalingskenmerken of één kenmerk per regel'
                : 'Paste text with payment references or one reference per line'}
            </p>
            <button
              onClick={handleDecode}
              disabled={loading || !input.trim()}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary dark:bg-accent text-white font-medium hover:bg-slate-800 dark:hover:bg-accent-hover disabled:opacity-50 transition-colors btn-press"
              data-testid="bulk-decode-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Layers className="w-5 h-5" />
                  {t('bulk_button')}
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Results */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Stats */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <div className="flex items-center gap-6">
                <span className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-900 dark:text-white">{stats?.total}</span> {t('bulk_results')}
                </span>
                <span className="flex items-center gap-1 text-sm text-success">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-semibold">{stats?.valid}</span> {t('bulk_valid')}
                </span>
                <span className="flex items-center gap-1 text-sm text-error">
                  <XCircle className="w-4 h-4" />
                  <span className="font-semibold">{stats?.invalid}</span> {t('bulk_invalid')}
                </span>
              </div>
              
              <div className="flex items-center gap-2 relative">
                {/* CSV Export */}
                <button
                  onClick={() => exportToCSV(results, null, language)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  data-testid="export-csv-btn"
                >
                  <FileDown className="w-4 h-4" />
                  CSV
                </button>
                
                {/* Excel Export with dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
                    data-testid="export-excel-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel
                    <ChevronDown className={`w-4 h-4 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 py-2 z-50">
                      <button
                        onClick={() => {
                          exportToExcel(results, null, language);
                          setShowExportMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        {language === 'nl' ? 'Alle resultaten' : 'All results'}
                        <span className="block text-xs text-slate-400">{language === 'nl' ? 'Met samenvatting' : 'With summary'}</span>
                      </button>
                      {stats?.valid > 0 && (
                        <button
                          onClick={() => {
                            exportValidOnly(results, 'xlsx', language);
                            setShowExportMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          {language === 'nl' ? 'Alleen geldige' : 'Valid only'}
                          <span className="block text-xs text-slate-400">{stats?.valid} {language === 'nl' ? 'kenmerken' : 'references'}</span>
                        </button>
                      )}
                      {getTaxTypes().length > 1 && (
                        <>
                          <div className="border-t border-slate-100 dark:border-slate-700 my-2" />
                          <div className="px-4 py-1 text-xs font-medium text-slate-400 uppercase">
                            {language === 'nl' ? 'Per belastingtype' : 'By tax type'}
                          </div>
                          {getTaxTypes().slice(0, 5).map(type => (
                            <button
                              key={type}
                              onClick={() => {
                                const filtered = results.filter(r => r.valid && r.belastingtype?.nl === type);
                                exportToExcel(filtered, null, language);
                                setShowExportMenu(false);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                            >
                              {type}
                              <span className="block text-xs text-slate-400">
                                {results.filter(r => r.valid && r.belastingtype?.nl === type).length} {language === 'nl' ? 'kenmerken' : 'refs'}
                              </span>
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Results table */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Kenmerk</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('result_tax_type')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('result_year')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('result_period')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {results.map((r, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-3">
                          {r.valid ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/10 text-success text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-error/10 text-error text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Error
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-slate-900 dark:text-white">
                          {r.kenmerk}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {r.input_type === 'aanslagnummer' 
                            ? (language === 'nl' ? 'Aanslag' : 'Assessment')
                            : (language === 'nl' ? 'Betaling' : 'Payment')}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.belastingtype?.[language] || r.error || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.jaar || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.tijdvak || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );

  if (!isPro()) {
    return (
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProFeatureGate feature={language === 'nl' ? 'Bulk decoder voor meerdere kenmerken tegelijk' : 'Bulk decoder for multiple references at once'}>
            {content}
          </ProFeatureGate>
        </div>
      </div>
    );
  }

  return content;
};

export default BulkPage;
